import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { CorporationUploadFileModal } from '../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { useServiceRequestFile } from '../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { updateTaskStatus, viewUploadedFile } from '../../utils';

export const EINLetter = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const [file, setFile] = useServiceRequestFile(
    serviceRequest,
    LegacyDocumentType.EIN
  );

  const einDocument = serviceRequest?.corporation?.corporationDocuments?.find(
    (each) => each?.documentType === LegacyDocumentType.EIN
  );

  const uploadModalVisibility = useToggle();

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: einDocument?.files?.[0]?.id ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={async () =>
                  viewUploadedFile(einDocument?.files?.[0]?.id)
                }
              >
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                status={task.status}
                label="Employer Identification Number (EIN) Letter uploaded by Commenda"
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads Employer Identification Number (EIN) Letter" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads Employer Identification Number (EIN) Letter" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: einDocument?.files?.[0]?.id ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={async () =>
                  viewUploadedFile(einDocument?.files?.[0]?.id)
                }
              >
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                status={task.status}
                label="Employer Identification Number (EIN) Letter uploaded"
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadModalVisibility.toggle}
                variant="primary"
              >
                Upload <Icons.Upload01 className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload Employer Identification Number (EIN) Letter" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled onClick={() => {}} variant="primary">
                Upload <Icons.Upload01 className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload Employer Identification Number (EIN) Letter" />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        serviceRequestTaskId={task.id}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        file={file}
        setFile={setFile}
        heading="Upload Employer Identification Number (EIN) Letter"
        subheading="This document will be saved in the client’s profile."
        documentType={LegacyDocumentType.EIN}
        onUploadSuccess={() =>
          updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          })
        }
      />
    </div>
  );
};
